import { useOfferFiltersStore, type OfferFilterState } from '@/stores/offerFilters';
import { UrlParameterService, type UrlParameters } from '@/services/url/urlParameterService';
import { loadProductId } from '@/composables/offers/useProductId';
import { loadSearchWidgetDefaults } from '@/composables/filters/useSearchWidgetDefaults';
import { SessionStorageService } from '@/services/storage/sessionStorageService';
import { nightsInRange, resolveDurationIntent } from '@/utils/filters/durationMode';
import { MAX_PRODUCT_DURATION } from '@/utils/filters/durationPlan';
import { formatDateLocal } from '@/utils/formatting/dateFormatter';

export function useOfferInitializer() {
  const filtersStore = useOfferFiltersStore();

  async function initializeFilters(): Promise<void> {
    const { initialState: defaults } = loadSearchWidgetDefaults();
    const filterState: OfferFilterState = {
      dateRange: defaults.dateRange,
      duration: defaults.duration,
      roomOccupancy: defaults.roomOccupancy,
    };

    const productId = loadProductId();
    if (productId) {
      filterState.productCode = productId;
    } else {
      console.warn('[useOfferInitializer] No productId found in HTML');
    }

    const urlParams = UrlParameterService.parseUrlParameters();
    const urlFilterState = mapUrlParamsToFilterState(urlParams);

    Object.assign(filterState, urlFilterState);

    const ss = SessionStorageService.read();
    filterState.durationMode = resolveDurationIntent(urlParams.durationMode, ss?.durationMode);

    // Same guard as useFilterInitializer: no product runs longer than 21 nights.
    if (
      filterState.durationMode === 'exact' &&
      nightsInRange(filterState.dateRange ?? null) > MAX_PRODUCT_DURATION
    ) {
      filterState.durationMode = 'preset';
    }

    if (Object.keys(filterState).length > 0) {
      filtersStore.setInitialState(filterState);
      filtersStore.hasUserInteracted = false;
      filtersStore.hasTravelDatesInteracted = false;
      filtersStore.hasPaxInteracted = false;
    } else {
      console.warn('[useOfferInitializer] No initialization parameters found');
    }
  }

  function mapUrlParamsToFilterState(params: UrlParameters): OfferFilterState {
    const state: OfferFilterState = {};

    if (params.startDate && params.endDate) {
      state.dateRange = [params.startDate, params.endDate];
    }

    if (params.duration) {
      state.duration = params.duration;
    }

    if (params.roomOccupancy) {
      state.roomOccupancy = params.roomOccupancy;
    }

    if (params.offerStartDate && params.offerEndDate) {
      state.anchorStartDate = formatDateLocal(params.offerStartDate);
    }

    return state;
  }
  return {
    initializeFilters,
  };
}
